// Full display snapshots, never offer authority. Rendering the same AthleteCard
// before and after verification avoids swapping a headshot skeleton for a card.
// These JSON response rows are copied so card state cannot mutate the snapshot.
const copyRows = (rows) => JSON.parse(JSON.stringify(rows));
const isLive = (row) => row.is_live === true || row.is_live === 1 || row.is_live === "1";
const PREGAME_PREVIEW_TTL_MS = 5 * 60 * 1000;
const LIVE_PREVIEW_TTL_MS = 2 * 60 * 1000;
// Feed invalidation keeps the last display while a fresh request verifies it.
// Viewer/market identity, age (five minutes pregame, two live), and event start bound reuse.
const createBoardVisualCache = ({ now = () => Date.now() } = {}) => {
  const entries = new Map();
  return {
    set(key, rows) {
      if (!key) return;
      if (Array.isArray(rows) && rows.some(row => row?._boardOptimisticPreview)) return;
      entries.delete(key);
      if (!Array.isArray(rows) || !rows.length) return;
      entries.set(key, { at: now(), rows: copyRows(rows.slice(0, 15)) });
      while (entries.size > 24) entries.delete(entries.keys().next().value);
    },
    get(key) {
      const entry = entries.get(key);
      const time = now();
      if (!entry || time < entry.at || time - entry.at >= PREGAME_PREVIEW_TTL_MS) return [];
      return copyRows(entry.rows.filter(row => isLive(row)
        ? time - entry.at < LIVE_PREVIEW_TTL_MS
        : Date.parse(row.start_time_iso || row.start_time_obj || "") > time))
        .map(row => ({ ...row, _boardOptimisticPreview: true }));
    },
    clear() { entries.clear(); },
  };
};

// Persistence is a reload optimization. Keep it outside the response/paint path.
const scheduleAfterBoardPaint = (callback) => {
  let cancelled = false;
  let idleId;
  let timerId;
  const frameId = window.requestAnimationFrame(() => {
    if (typeof window.requestIdleCallback === "function") {
      idleId = window.requestIdleCallback(() => !cancelled && callback(), { timeout: 1500 });
    } else {
      timerId = window.setTimeout(() => !cancelled && callback(), 32);
    }
  });
  return () => {
    cancelled = true;
    window.cancelAnimationFrame(frameId);
    if (idleId !== undefined) window.cancelIdleCallback(idleId);
    if (timerId !== undefined) window.clearTimeout(timerId);
  };
};

const createDeferredBoardPersistence = ({ write, schedule = scheduleAfterBoardPaint, maxEntries = 24 }) => {
  const pending = new Map();
  let cancelScheduled = null;
  let generation = 0;
  return {
    enqueue(key, entry) {
      pending.delete(key);
      pending.set(key, entry);
      while (pending.size > maxEntries) pending.delete(pending.keys().next().value);
      if (cancelScheduled) return;
      const scheduledGeneration = generation;
      cancelScheduled = schedule(() => {
        if (scheduledGeneration !== generation) return;
        cancelScheduled = null;
        const entries = Array.from(pending);
        pending.clear();
        try { write(entries); } catch (error) {
          // Storage failures cannot interfere with a verified, rendered board.
        }
      });
    },
    clear() {
      generation += 1;
      cancelScheduled?.();
      cancelScheduled = null;
      pending.clear();
    },
  };
};

module.exports = { createBoardVisualCache, createDeferredBoardPersistence };
